import {
  DatePicker,
  DatePickerProps,
  Form,
  Input,
  InputProps,
  Select,
  SelectProps,
  Tooltip,
} from "antd-v5";
import { FormItemProps } from "antd-v5/lib";
import { FormInstance } from "antd/lib/form";
import SelectWithPaginatedData from "components/SelectWithPaginatedData";
import Vocabulary from "components/Vocabulary";
import NewCVSSCalculator from "components/cvss/NewCVSSCalculator";
import dayjs from "dayjs";
import useModalRenderer from "hooks/useModalRenderer";
import useSeveritiesOptions from "hooks/useSeveritiesOptions";
import { isEmpty } from "lodash";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import UserAPI from "restAPI/UserAPI";
import { DraftFindingResponse } from "types/api/draftFindingResponse";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";
import { FilterOptionLabelValue } from "types/pagination";
import {
  cvssVectorValidator,
  getCvssScoreFromVector,
  validateCVSSVectorFormat,
} from "utils/cvssUtil";
import { parseSelectOptions } from "utils/formUtils";
import { CellRenderers } from "utils/tableUtils";
import {
  EXPLOITED_OPTIONS,
  TOOL_FINDING_CATEGORIES,
  scanTypeOptions,
} from "views/Findings/Constants";

import CalculatorIcon from "ac-components/ac-icons/CalculatorIcon";
import ACMetadataPanel from "ac-components/ac-metadata-panel/ACMetadataPanel";

import { DraftFindingFormData } from "./DraftFindingForm";

export default function DraftFindingFormMetaDataSection({
  currentDraftData,
  preMetaSection,
  preMetaData,
  hiddenFields,
  form,
  triggerValuesChange,
  draftId,
}: {
  currentDraftData?: DraftFindingResponse;
  preMetaSection?: ReactNode | ReactNode[];
  hiddenFields?: string[];
  form: FormInstance<DraftFindingFormData>;
  triggerValuesChange: (values: Partial<DraftFindingResponse>) => void;
  draftId: string;
  preMetaData?: {
    subProductId: number | undefined;
    envName: string | undefined;
  };
}) {
  const severitiesOptions = useSeveritiesOptions({
    valueCase: "upper",
  });
  const [users, setUsers] = useState<Array<FilterOptionLabelValue>>();
  const [isDisabled, setIsDisabled] = useState(false);
  const modalRenderer = useModalRenderer();

  useEffect(() => {
    setIsDisabled(false);
    const { promise, cancel } = UserAPI.getAll();
    promise.then((userData) => {
      setUsers(
        userData.map((d: UserInfoProjectorImpl) => ({
          value: d.id || "",
          label: d.displayName || "",
        })),
      );
    });
    return cancel;
  }, [draftId]);

  const showCVSSCalculator = useCallback(() => {
    const data = form.getFieldsValue();
    modalRenderer.show({
      content: (
        <NewCVSSCalculator
          //@ts-expect-error Type 'string | undefined' is not assignable to type 'string'.
          initialCvssVector={
            validateCVSSVectorFormat(data.cvssVector) ? data.cvssVector : ""
          }
          selectedCVE={form.getFieldValue("cves")}
          onSubmit={(formData) => {
            if (!isEmpty(formData)) {
              const cvssValues = {
                baseScore: formData.baseScore,
                cvssVector: formData.vectorString,
              };
              form.setFieldsValue(cvssValues);
              triggerValuesChange(cvssValues);
              setIsDisabled(true);
            }
          }}
          finding={{
            subProduct: { id: preMetaData?.subProductId },
            envName: preMetaData?.envName,
          }}
        />
      ),
      width: 1056,
    });
  }, [form, triggerValuesChange, modalRenderer, preMetaData]);

  return (
    <ACMetadataPanel>
      {preMetaSection}
      <ACMetadataPanel.Section title="Vulnerability">
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>
            <Vocabulary name="severity" />
          </ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            {" "}
            <ValueSelect options={severitiesOptions} name="severity" />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>Scan Type</ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            <ValueSelect
              options={parseSelectOptions(scanTypeOptions)}
              name="scanType"
            />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        {!hiddenFields?.includes("scanDate") ? (
          <ACMetadataPanel.Row>
            <ACMetadataPanel.Label>Scan Date</ACMetadataPanel.Label>
            <ACMetadataPanel.Value>
              <ValueDatePicker
                name="scanDate"
                disabledDate={(current) => current?.isAfter(dayjs())}
              />
            </ACMetadataPanel.Value>
          </ACMetadataPanel.Row>
        ) : null}
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>
            Tool <Vocabulary name="category" />
          </ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            {" "}
            <ValueSelect
              options={parseSelectOptions(TOOL_FINDING_CATEGORIES)}
              name="category"
            />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>CWE</ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            <CWEInput cwes={currentDraftData?.cwes} />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>CVE</ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            <CVEInput />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>CVSS Vector</ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            <div className="d-flex align-items-center">
              <ValueInput
                name="cvssVector"
                rules={[{ ...cvssVectorValidator() }]}
                disabled={isDisabled || currentDraftData?.outOfBox}
                onChange={(e) => {
                  const vector = e.target.value;
                  const score = getCvssScoreFromVector(vector);
                  form.setFieldsValue({ baseScore: score });
                  triggerValuesChange({ cvssVector: vector, baseScore: score });
                }}
              />

              <Tooltip
                title={
                  isDisabled
                    ? 'Note: "CVSS Vector" and "Base Score" are added via calculator, click "CVSS Calculator" to update.'
                    : "CVSS Calculator"
                }
                placement="left"
              >
                <CalculatorIcon
                  onClick={
                    currentDraftData?.outOfBox ? undefined : showCVSSCalculator
                  }
                  style={{ fontSize: 16 }}
                  className={currentDraftData?.outOfBox ? "disabled" : ""}
                />
              </Tooltip>
            </div>
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>Base Score</ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            <ValueInput
              type="number"
              name="baseScore"
              disabled={isDisabled || currentDraftData?.outOfBox}
            />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        {!hiddenFields?.includes("exploited") ? (
          <ACMetadataPanel.Row>
            <ACMetadataPanel.Label>Exploit Available</ACMetadataPanel.Label>
            <ACMetadataPanel.Value>
              <ValueSelect
                options={parseSelectOptions(EXPLOITED_OPTIONS)}
                name="exploited"
              />
            </ACMetadataPanel.Value>
          </ACMetadataPanel.Row>
        ) : null}
      </ACMetadataPanel.Section>
      <ACMetadataPanel.Section title="Component Details">
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>Name</ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            <ValueInput name="componentName" />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>Affected Version</ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            <ValueInput name="componentVersion" />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
        <ACMetadataPanel.Row>
          <ACMetadataPanel.Label>Fix Version</ACMetadataPanel.Label>
          <ACMetadataPanel.Value>
            <ValueInput name="componentFixVersions" />
          </ACMetadataPanel.Value>
        </ACMetadataPanel.Row>
      </ACMetadataPanel.Section>
      {!hiddenFields?.includes("ownerId") ? (
        <ACMetadataPanel.Section title="Owner">
          <ACMetadataPanel.Row>
            <ACMetadataPanel.Label>
              <Vocabulary name="owner" />
            </ACMetadataPanel.Label>
            <ACMetadataPanel.Value>
              <ValueSelect
                options={users}
                name="ownerId"
                showSearch
                optionFilterProp="label"
              />
            </ACMetadataPanel.Value>
          </ACMetadataPanel.Row>
        </ACMetadataPanel.Section>
      ) : null}
      {!hiddenFields?.includes("vmHostName") ? (
        <ACMetadataPanel.Section title="Others">
          <ACMetadataPanel.Row>
            <ACMetadataPanel.Label>Device</ACMetadataPanel.Label>
            <ACMetadataPanel.Value>
              <ValueInput name="vmHostName" />
            </ACMetadataPanel.Value>
          </ACMetadataPanel.Row>
        </ACMetadataPanel.Section>
      ) : null}
      {modalRenderer.context}
    </ACMetadataPanel>
  );
}

export const ValueSelect = ({
  name,
  ...props
}: Pick<SelectProps, "options" | "optionFilterProp" | "showSearch"> & {
  name: string;
}) => {
  return (
    <Form.Item name={name} noStyle>
      <Select
        variant="borderless"
        className="meta-data-select-menu"
        options={[]}
        size="small"
        allowClear
        placeholder={CellRenderers.VALUE_OR_NA()}
        popupMatchSelectWidth={false}
        {...props}
      />
    </Form.Item>
  );
};

const ValueInput = ({
  name,
  rules,
  ...props
}: InputProps & { name: string } & Pick<FormItemProps, "rules">) => {
  return (
    <Form.Item name={name} rules={rules ? rules : []}>
      <Input
        variant="borderless"
        className="p-0"
        size="small"
        allowClear
        placeholder={CellRenderers.VALUE_OR_NA()}
        {...props}
      />
    </Form.Item>
  );
};

const ValueDatePicker = ({
  name,
  ...props
}: DatePickerProps & { name: string }) => {
  return (
    <Form.Item name={name} noStyle>
      <DatePicker
        variant="borderless"
        placeholder={CellRenderers.VALUE_OR_NA()}
        className="p-0 w-100"
        {...props}
      />
    </Form.Item>
  );
};

const CVEInput = () => {
  return (
    <Form.Item name="cves" noStyle>
      <SelectWithPaginatedData
        api={FindingAPI.getCVEs}
        mode="multiple"
        bordered={false}
        size="small"
        className="meta-data-select-menu"
        placeholder={CellRenderers.VALUE_OR_NA()}
      />
    </Form.Item>
  );
};
const CWEInput = ({ cwes }: { cwes?: number[] }) => {
  const getOptions = useCallback(
    (params) => {
      const { promise, cancel } = FindingAPI.getCWEs(params);
      return {
        cancel,
        promise: promise.then((response: any) => {
          return [...response.content, ...(cwes || [])].sort();
        }),
      };
    },
    [cwes],
  );
  return (
    <Form.Item name="cwes" noStyle>
      <SelectWithPaginatedData
        api={getOptions}
        mode="multiple"
        bordered={false}
        size="small"
        className="meta-data-select-menu"
        parseSelectOptions={parseCWEOptions}
        resetOptionsProp={cwes}
        placeholder={CellRenderers.VALUE_OR_NA()}
      />
    </Form.Item>
  );
};
export const parseCWEOptions = (cwes: string[]) => {
  return cwes.map((cwe) => ({ label: `CWE-${cwe}`, value: +cwe }));
};
